"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getQueryCompleteResult = exports.default = void 0;
exports.useDebounce = useDebounce;
var _react = require("react");
var self = _interopRequireWildcard(require("./hooks"));
var _utilities = require("./utilities");
var _utils = require("@site/utils");
var _domainServices = require("@site/domain-services");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/*
use a debounce to limit the frequency of calling useEffect for any changes to value
*/
function useDebounce({
  queryString,
  delay
}) {
  const [debouncedValue, setDebouncedValue] = (0, _react.useState)(queryString);
  (0, _react.useEffect)(() => {
    if (delay) {
      // Set debouncedValue to the value passed-in after the specified delay
      const handler = setTimeout(() => {
        setDebouncedValue(queryString);
      }, delay);

      /*  https://reactjs.org/docs/hooks-effect.html
      this cleans up effects from the previous render before running the effects next time.
      for our case, each time user types in new query string within the delay period, we clear the timer
      before re-running the setTimeout. That way, we prevent invoking setDebouncedValue until user has stopped
      typing for more than delay ms
      */
      return () => {
        clearTimeout(handler);
      };
    }
    setDebouncedValue(queryString);
  }, [queryString]);
  return debouncedValue;
}
const useAutoComplete = ({
  autocompleteMode,
  env,
  apiKey,
  plid,
  market,
  onError,
  query,
  domainNamesFetchCallback,
  debounceDelay,
  apiVersion,
  resellerBaseOverride,
  apm
}) => {
  const [data, setData] = (0, _react.useState)([]);
  /*
   * https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
   * The useRef() Hook isn’t just for DOM refs.
   * The “ref” object is a generic container whose current property is mutable and can hold any value,
   * similar to an instance property on a class.
   */
  const webSocket = (0, _react.useRef)(null);
  const sendQuery = () => {
    const domainCompleteSpan = apm?.startSpan?.('domainCompleteSpan');
    webSocket?.current?.send(JSON.stringify({
      q: query,
      country_site: market
    }));
    domainCompleteSpan?.end();
  };
  const initWebSocket = () => {
    webSocket.current = new WebSocket((0, _utilities.getDomainCompleteApiUrl)(env));
    webSocket.current.onopen = sendQuery;
    webSocket.current.onmessage = message => {
      let newData = [];
      try {
        newData = JSON.parse(message?.data)?.suggestions?.tldComplete || [];
      } catch (err) {
        typeof onError === 'function' && onError(err);
      }
      setData(newData);
    };
    webSocket.current.onerror = err => {
      typeof onError === 'function' && onError(err);
      setData([]);
    };
    webSocket.current.onclose = () => {
      webSocket.current = null;
    };
  };

  // debounce or limit reacting to changes in query in useEffect
  const debouncedOrActualQuery = useDebounce({
    queryString: query,
    delay: debounceDelay
  });
  (0, _react.useEffect)(() => {
    // disable domain complete for reseller
    if (autocompleteMode === 'domain' && !(0, _utils.isReseller)(plid)) {
      if (!webSocket?.current) {
        initWebSocket();
      } else if (webSocket.current.readyState !== WebSocket.CONNECTING) {
        // otherwise will be sent on open
        sendQuery();
      }
    } else if (autocompleteMode === 'query') {
      const version = Number(apiVersion) > 1 ? `/v${apiVersion}` : '';
      self.getQueryCompleteResult({
        env,
        market,
        apiKey,
        onError,
        plid,
        query: debouncedOrActualQuery,
        setData,
        version,
        resellerBaseOverride,
        apm
      });
    }
  }, [plid, debouncedOrActualQuery]);
  (0, _react.useEffect)(() => {
    // close web socket on unmount
    return () => {
      if (webSocket?.current?.close) {
        webSocket.current.close();
      }
    };
  }, []);
  (0, _react.useEffect)(() => {
    typeof domainNamesFetchCallback === 'function' && domainNamesFetchCallback(data);
  }, [data]);
  return data;
};
var _default = exports.default = useAutoComplete;
const getQueryCompleteResult = ({
  env,
  market,
  apiKey = 'dpp_search',
  plid,
  query,
  version,
  resellerBaseOverride,
  onError,
  setData,
  apm
}) => {
  const queryCompleteSpan = apm?.startSpan?.('queryCompleteSpan');
  const {
    getQueryCompletePromise
  } = new _domainServices.DomainServices({
    fetchEntity: global.fetch || {},
    apiKey,
    market,
    env,
    plid,
    resellerBaseOverride
  });
  getQueryCompletePromise(query, version).then(res => {
    queryCompleteSpan?.end();
    if (!res.ok) {
      throw Error(res.statusText);
    }
    return res.json();
  }).then(json => setData(json?.r || [])).catch(error => {
    typeof onError === 'function' && onError(error);
    setData([]);
  });
};
exports.getQueryCompleteResult = getQueryCompleteResult;